import { useState, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { PartnerAsset, AssetPage, CreateAssetData, UpdateAssetData } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  FileText,
  Upload,
  GripVertical,
  Eye,
  EyeOff,
  AlertCircle,
  Link as LinkIcon,
} from 'lucide-react';

// Helper to format file size
function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Helper to get file type label
function getFileTypeLabel(mimeType: string | null): string {
  if (!mimeType) return 'File';
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'DOC';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'XLS';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'PPT';
  if (mimeType.includes('image')) return 'Image';
  if (mimeType.includes('video')) return 'Video';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'ZIP';
  return 'File';
}

type AssetFormData = {
  title: string;
  description: string;
  category: string;
  type: 'external_link' | 'file';
  external_url: string;
  is_published: boolean;
  file?: File;
  s3_key?: string;
  file_name?: string;
  mime_type?: string;
  file_size?: number;
};

const defaultFormData: AssetFormData = {
  title: '',
  description: '',
  category: '',
  type: 'external_link',
  external_url: '',
  is_published: true,
};

export function AdminAssetsPage() {
  const [activeTab, setActiveTab] = useState<AssetPage>('sales_marketing');
  const [assets, setAssets] = useState<PartnerAsset[]>([]);
  const [allCategories, setAllCategories] = useState<{
    sales_marketing: string[];
    training: string[];
  }>({ sales_marketing: [], training: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<PartnerAsset | null>(null);
  const [formData, setFormData] = useState<AssetFormData>(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // Delete confirmation
  const [deleteAsset, setDeleteAsset] = useState<PartnerAsset | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getAdminAssets();
      setAssets(response.assets);
      setAllCategories(response.categories);
    } catch (err) {
      console.error('Failed to load assets:', err);
      setError('Failed to load assets');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAssets = assets.filter((asset) => asset.page === activeTab);
  const currentCategories = allCategories[activeTab];

  const openCreateDialog = () => {
    setEditingAsset(null);
    setFormData({ ...defaultFormData });
    setIsDialogOpen(true);
  };

  const openEditDialog = (asset: PartnerAsset) => {
    setEditingAsset(asset);
    setFormData({
      title: asset.title,
      description: asset.description || '',
      category: asset.category || '',
      type: asset.type,
      external_url: asset.external_url || '',
      is_published: asset.is_published,
      s3_key: asset.s3_key || undefined,
      file_name: asset.file_name || undefined,
      mime_type: asset.mime_type || undefined,
      file_size: asset.file_size || undefined,
    });
    setIsDialogOpen(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        file,
        file_name: file.name,
        mime_type: file.type,
        file_size: file.size,
      }));
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    // Get pre-signed upload URL
    const { upload_url, s3_key } = await api.getUploadUrl(
      file.name,
      file.type,
      file.size
    );

    // Upload file directly to S3
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(s3_key);
        } else {
          reject(new Error('Upload failed'));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', upload_url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setUploadProgress(null);
    setError(null);

    try {
      let s3Key = formData.s3_key;

      // If it's a file type and there's a new file, upload it first
      if (formData.type === 'file' && formData.file) {
        s3Key = await uploadFile(formData.file);
      }

      if (editingAsset) {
        // Update existing asset
        const updateData: UpdateAssetData = {
          title: formData.title,
          description: formData.description || null,
          category: formData.category || null,
          is_published: formData.is_published,
        };

        if (formData.type === 'external_link') {
          updateData.external_url = formData.external_url;
        }

        await api.updateAsset(editingAsset.id, updateData);
      } else {
        // Create new asset
        const createData: CreateAssetData = {
          title: formData.title,
          description: formData.description || undefined,
          page: activeTab,
          category: formData.category || undefined,
          type: formData.type,
          is_published: formData.is_published,
        };

        if (formData.type === 'external_link') {
          createData.external_url = formData.external_url;
        } else if (formData.type === 'file' && s3Key) {
          createData.s3_key = s3Key;
          createData.file_name = formData.file_name;
          createData.mime_type = formData.mime_type;
          createData.file_size = formData.file_size;
        }

        await api.createAsset(createData);
      }

      setIsDialogOpen(false);
      await loadAssets();
    } catch (err: unknown) {
      console.error('Failed to save asset:', err);
      const apiError = err as { error?: string };
      setError(apiError.error || 'Failed to save asset');
    } finally {
      setIsSaving(false);
      setUploadProgress(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteAsset) return;

    setIsDeleting(true);
    try {
      await api.deleteAsset(deleteAsset.id);
      setDeleteAsset(null);
      await loadAssets();
    } catch (err) {
      console.error('Failed to delete asset:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const togglePublished = async (asset: PartnerAsset) => {
    try {
      await api.updateAsset(asset.id, { is_published: !asset.is_published });
      await loadAssets();
    } catch (err) {
      console.error('Failed to toggle published state:', err);
    }
  };

  const isFormValid = () => {
    if (!formData.title.trim()) return false;
    if (formData.type === 'external_link' && !formData.external_url.trim()) return false;
    if (formData.type === 'file' && !editingAsset && !formData.file) return false;
    return true;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Partner Assets</h1>
            <p className="text-gray-600 mt-1">
              Manage resources for reseller partners
            </p>
          </div>
          <Button onClick={openCreateDialog} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Asset
          </Button>
        </div>

        {/* Error Banner */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as AssetPage)}>
          <TabsList>
            <TabsTrigger value="sales_marketing">
              Sales & Marketing
              <span className="ml-2 bg-gray-200 text-gray-700 text-xs px-1.5 py-0.5 rounded">
                {assets.filter((a) => a.page === 'sales_marketing').length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="training">
              Training
              <span className="ml-2 bg-gray-200 text-gray-700 text-xs px-1.5 py-0.5 rounded">
                {assets.filter((a) => a.page === 'training').length}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {filteredAssets.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No assets yet.</p>
                  <Button
                    variant="link"
                    onClick={openCreateDialog}
                    className="mt-2"
                  >
                    Add your first asset
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredAssets.map((asset) => (
                  <Card key={asset.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        {/* Drag handle (placeholder) */}
                        <div className="text-gray-300 cursor-grab">
                          <GripVertical className="h-5 w-5" />
                        </div>

                        {/* Type icon */}
                        <div className="flex-shrink-0">
                          {asset.type === 'external_link' ? (
                            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <LinkIcon className="h-5 w-5 text-blue-600" />
                            </div>
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                              <FileText className="h-5 w-5 text-gray-600" />
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900 truncate">
                              {asset.title}
                            </h3>
                            {!asset.is_published && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                Draft
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                            {asset.category && (
                              <>
                                <span>{asset.category}</span>
                                <span className="text-gray-300">|</span>
                              </>
                            )}
                            {asset.type === 'file' ? (
                              <>
                                <span>{getFileTypeLabel(asset.mime_type)}</span>
                                {asset.file_size && (
                                  <span className="text-gray-300">
                                    {formatFileSize(asset.file_size)}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="truncate max-w-xs">
                                {asset.external_url}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => togglePublished(asset)}
                            title={asset.is_published ? 'Unpublish' : 'Publish'}
                          >
                            {asset.is_published ? (
                              <Eye className="h-4 w-4 text-green-600" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(asset)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteAsset(asset)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingAsset ? 'Edit Asset' : 'Add Asset'}
            </DialogTitle>
            <DialogDescription>
              {editingAsset
                ? 'Update the asset details below.'
                : `Add a new asset to ${activeTab === 'sales_marketing' ? 'Sales & Marketing' : 'Training'}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Type selector (only for new assets) */}
            {!editingAsset && (
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) =>
                    setFormData((prev) => ({
                      ...prev,
                      type: v as 'external_link' | 'file',
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external_link">External Link</SelectItem>
                    <SelectItem value="file">File Upload</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Enter asset title"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Brief description of this resource"
                rows={3}
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category (optional)</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, category: e.target.value }))
                }
                placeholder="e.g., Brochures, Videos, Guides"
                list="categories"
              />
              {currentCategories.length > 0 && (
                <datalist id="categories">
                  {currentCategories.map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              )}
            </div>

            {/* External URL (for links) */}
            {formData.type === 'external_link' && (
              <div className="space-y-2">
                <Label htmlFor="external_url">URL</Label>
                <Input
                  id="external_url"
                  type="url"
                  value={formData.external_url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      external_url: e.target.value,
                    }))
                  }
                  placeholder="https://example.com/resource"
                />
              </div>
            )}

            {/* File upload */}
            {formData.type === 'file' && (
              <div className="space-y-2">
                <Label>File</Label>
                {editingAsset && formData.file_name && !formData.file && (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg mb-2">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-700">
                      {formData.file_name}
                    </span>
                    <span className="text-sm text-gray-500">
                      ({formatFileSize(formData.file_size || 0)})
                    </span>
                  </div>
                )}
                {formData.file && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg mb-2">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-700">
                      {formData.file.name}
                    </span>
                    <span className="text-sm text-green-600">
                      ({formatFileSize(formData.file.size)})
                    </span>
                    <button
                      onClick={() => setFormData((prev) => ({ ...prev, file: undefined }))}
                      className="ml-auto text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {editingAsset ? 'Replace File' : 'Choose File'}
                </Button>
              </div>
            )}

            {/* Published toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="is_published">Published</Label>
                <p className="text-sm text-gray-500">
                  When published, resellers can see this asset
                </p>
              </div>
              <Switch
                id="is_published"
                checked={formData.is_published}
                onCheckedChange={(checked: boolean) =>
                  setFormData((prev) => ({ ...prev, is_published: checked }))
                }
              />
            </div>

            {/* Upload progress */}
            {uploadProgress !== null && (
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-abby-primary transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!isFormValid() || isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingAsset ? 'Save Changes' : 'Create Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteAsset} onOpenChange={() => setDeleteAsset(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Asset</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteAsset?.title}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteAsset(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
